import { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { updateCustomerDiscount } from "../../../services";

interface DiscountModalProps {
  open: boolean;
  customerUuid: string;
  customerName: string;
  customerCode: string;
  /** Current agreement, or null when the shop has none. */
  discountPercent: number | null;
  onClose: () => void;
  onSaved: () => void;
}

// The server's own bounds (customer.validate.config.ts): a discount is a real
// percentage or it is nothing, and "nothing" is expressed by clearing the field —
// never by typing 0.
const MIN_PERCENT = 0.01;
const MAX_PERCENT = 100;

export default function DiscountModal({
  open,
  customerUuid,
  customerName,
  customerCode,
  discountPercent,
  onClose,
  onSaved,
}: DiscountModalProps) {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);

  // Reset to the stored value every time the dialog opens, so a cancelled edit
  // never leaks into the next one.
  useEffect(() => {
    if (open) setValue(discountPercent != null ? String(discountPercent) : "");
  }, [open, discountPercent]);

  const trimmed = value.trim();
  const isClearing = trimmed.length === 0;
  const parsed = Number(trimmed);

  const error = useMemo(() => {
    if (isClearing) return null;
    if (!Number.isFinite(parsed)) return "กรอกเป็นตัวเลขเท่านั้น";
    if (parsed < MIN_PERCENT || parsed > MAX_PERCENT)
      return `ส่วนลดต้องอยู่ระหว่าง ${MIN_PERCENT} – ${MAX_PERCENT}%`;
    return null;
  }, [isClearing, parsed]);

  // Nothing to send when the field is left exactly as it was found.
  const isUnchanged = isClearing
    ? discountPercent == null
    : parsed === discountPercent;

  const handleSave = async () => {
    if (error || isUnchanged) return;
    setLoading(true);
    try {
      await updateCustomerDiscount(customerUuid, isClearing ? null : parsed);
      onSaved();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        ส่วนลดท้ายบิล
        <Typography variant="caption" color="text.secondary" display="block">
          {customerName} · {customerCode}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack gap={2}>
          <TextField
            autoFocus
            fullWidth
            label="ส่วนลด"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            error={Boolean(error)}
            helperText={error ?? "เว้นว่างไว้ = ไม่มีส่วนลด"}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              },
            }}
          />

          <Box sx={{ display: "flex", gap: 1, color: "text.secondary" }}>
            <InfoOutlinedIcon fontSize="small" sx={{ mt: "2px" }} />
            <Typography variant="caption">
              ลดเป็นเปอร์เซ็นต์ของราคาขายทั้งบิลตามสมุดราคา
              คิดภาษีมูลค่าเพิ่มจากยอดหลังหักส่วนลด
              มีผลกับบิลใบถัดไป บิลที่ออกไปแล้วไม่เปลี่ยน
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          ยกเลิก
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={Boolean(error) || isUnchanged}
          loading={loading}
        >
          บันทึก
        </Button>
      </DialogActions>
    </Dialog>
  );
}
